/**
 * Reads the email addresses out of the spreadsheet dropped onto the page.
 *
 * Deliberately forgiving about layout: the file might be a bare column of
 * addresses with no header, or a wider export where the addresses sit in one
 * column among many. Rather than demanding a particular column name, every sheet
 * is scanned and the column holding the most email-shaped values wins.
 */

import * as XLSX from "xlsx"

/**
 * Loose on purpose. The point is to identify which column holds addresses, not
 * to adjudicate whether each one is deliverable - an address that looks odd still
 * needs looking up and reporting on.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const looksLikeEmail = (value) => EMAIL_PATTERN.test(String(value || "").trim())

/**
 * Pick the column with the most email-shaped cells across the sheet's rows.
 */
const findEmailColumn = (rows) => {
	const hits = new Map()

	rows.forEach((row) => {
		(row || []).forEach((cell, columnIndex) => {
			if (!looksLikeEmail(cell)) return

			hits.set(columnIndex, (hits.get(columnIndex) || 0) + 1)
		})
	})

	if (hits.size === 0) return { columnIndex: -1, count: 0 }

	const [columnIndex, count] = [...hits.entries()].sort((a, b) => b[1] - a[1])[0]

	return { columnIndex, count }
}

/**
 * Decide whether the first row is a header. It is treated as one when it holds
 * no email in the chosen column but does hold text there.
 */
const detectHeader = (rows, columnIndex) => {
	const firstRow = rows[0] || []
	const cell = firstRow[columnIndex]

	return Boolean(String(cell || "").trim()) && !looksLikeEmail(cell)
}

/**
 * Parse an uploaded workbook into the list of addresses to process.
 *
 * Returns the addresses in file order, de-duplicated case-insensitively (the
 * first spelling seen is kept and any others are recorded), plus everything
 * needed to explain in the report what the source file contained.
 */
export const readEmailsFromWorkbook = async (file) => {
	const buffer = await file.arrayBuffer()
	const workbook = XLSX.read(buffer, { type: "array" })

	const found = new Map()
	const sheetsScanned = []

	let skippedRows = 0
	let totalRows = 0

	for (const sheetName of workbook.SheetNames) {
		const sheet = workbook.Sheets[sheetName]
		const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: "" })

		if (rows.length === 0) continue

		const { columnIndex, count } = findEmailColumn(rows)

		sheetsScanned.push({ sheetName, rows: rows.length, emailColumn: columnIndex, emailsFound: count })

		if (columnIndex === -1) continue

		const hasHeader = detectHeader(rows, columnIndex)
		const headers = hasHeader ? (rows[0] || []).map((cell) => String(cell || "").trim()) : []
		const dataRows = hasHeader ? rows.slice(1) : rows

		for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
			const row = dataRows[rowIndex] || []
			const raw = String(row[columnIndex] || "").trim()

			// Excel row number as the operator sees it in the file
			const excelRow = hasHeader ? rowIndex + 2 : rowIndex + 1

			if (!raw) continue

			totalRows += 1

			if (!looksLikeEmail(raw)) {
				skippedRows += 1
				continue
			}

			const key = raw.toLowerCase()

			if (found.has(key)) {
				const existing = found.get(key)
				existing.duplicateOf.push(`${sheetName}!row ${excelRow}`)

				if (!existing.spellings.includes(raw)) existing.spellings.push(raw)

				continue
			}

			// Keep the rest of the row so the outputs can echo whatever context
			// the source file carried, such as a name or a case reference
			const context = {}

			row.forEach((cell, cellIndex) => {
				if (cellIndex === columnIndex) return

				const value = String(cell || "").trim()

				if (!value) return

				context[headers[cellIndex] || `Column ${XLSX.utils.encode_col(cellIndex)}`] = value
			})

			found.set(key, {
				email: raw,
				normalised: key,
				spellings: [raw],
				source: `${sheetName}!row ${excelRow}`,
				duplicateOf: [],
				context,
			})
		}
	}

	return {
		emails: [...found.values()],
		sheetsScanned,
		skippedRows,
		totalRows,
		fileName: file.name,
		fileSize: file.size,
	}
}
